// Unsubscribe.tsx — bot-notifications design (docs/superpowers/specs/
// 2026-08-21-bot-notifications-design.md §"Unsubscribe / manage"), Task 5. `/unsubscribe?token=`,
// mounted OUTSIDE `<Protected>` in App.tsx next to `/invite/:token` — the visitor clicking this
// link from an email is, like an invite, exactly who this page is for whether or not they happen
// to have a session; `POST /api/notifications/unsubscribe` (Task 4) takes no auth at all, the
// verified token itself IS the credential.
//
// Same shell + StrictMode-safe "fire once" idiom as `InviteAccept.tsx`'s `acceptFiredRef`: the
// POST has no re-entrancy guard of its own, and `main.tsx` mounts the app under `<StrictMode>`,
// which runs an effect's first invocation twice in the same tick in dev — a `firedRef` (mutates
// synchronously, shared across both invocations) is what actually prevents a second POST, not
// gating on request status (which both invocations would read as the same pre-fire snapshot).
//
// Deliberately plain `apiFetch` + local `useState`, NOT `useMutation` (unlike InviteAccept.tsx's
// `acceptMutation`) — verified directly: `useMutation`'s `useSyncExternalStore`-based
// subscription can get orphaned by StrictMode's synthetic mount→cleanup→mount effect cycle when
// `.mutate()` is called from an effect during that FIRST pass — the mutation fires and its
// `mutationFn` really runs, but the resolution never reaches a re-render (repro'd in isolation:
// a bare `useMutation` + effect + `<StrictMode>` prints "mutationFn running" and then nothing —
// no success render, ever, even after a 2s `waitFor`). A raw `apiFetch` promise resolving into
// `useState` has no such subscription to lose; the identical `<StrictMode>` repro with local
// state resolves correctly on the very next tick. There's no caching upside to `useMutation` here
// either — this is a one-shot, unauthenticated, no-query-key action, not something another part
// of the tree needs to observe or invalidate.
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Skeleton } from '@repo/ui/components/skeleton';
import type { UnsubscribeResultDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';
import { useT } from '../locales/context';

/** Same centered, logo-topped shell as `InviteAccept.tsx`'s `InviteShell` — reached by the same
 * class of visitor (arriving from an email link, session unknown). */
function UnsubscribeShell({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">{children}</Card>
      </div>
    </div>
  );
}

function UnsubscribeSkeleton() {
  return (
    <UnsubscribeShell>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-4 w-full" />
      </CardContent>
    </UnsubscribeShell>
  );
}

/** Shared by both the tampered/expired-token case (a real 400 `INVALID_TOKEN` from the API) and
 * the missing-`?token=` case (no request is ever made) — same "no oracle" posture the API itself
 * takes on invalid tokens (routes/notifications.ts: one generic 400 regardless of why), so this
 * page doesn't invent a second, possibly-drifted distinction between "expired" and "just wrong". */
function ExpiredCard() {
  const t = useT();
  return (
    <UnsubscribeShell>
      <CardHeader>
        <CardTitle>{t.auth.unsubscribeLinkExpiredTitle}</CardTitle>
        <CardDescription>{t.auth.unsubscribeLinkExpiredBody}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <Link to="/">{t.auth.openDashboardCta}</Link>
        </Button>
      </CardContent>
    </UnsubscribeShell>
  );
}

type UnsubscribeState =
  | { status: 'pending' }
  | { status: 'success'; result: UnsubscribeResultDto }
  | { status: 'error' };

export function Unsubscribe() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<UnsubscribeState>({ status: 'pending' });

  const firedRef = useRef(false);
  useEffect(() => {
    if (!token || firedRef.current) return;
    firedRef.current = true;
    apiFetch<UnsubscribeResultDto>('/api/notifications/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((result) => setState({ status: 'success', result }))
      .catch(() => setState({ status: 'error' }));
  }, [token]);

  if (!token) return <ExpiredCard />;

  if (state.status === 'success') {
    const { event, botName } = state.result;
    return (
      <UnsubscribeShell>
        <CardHeader>
          <CardTitle>{t.auth.unsubscribedTitle}</CardTitle>
          <CardDescription>
            {t.auth.unsubscribedBody({ eventLabel: t.auth.eventLabels[event], botName })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/">{t.auth.openDashboardCta}</Link>
          </Button>
        </CardContent>
      </UnsubscribeShell>
    );
  }

  if (state.status === 'error') return <ExpiredCard />;

  // Pending (the common case — the effect above fires almost immediately) or the brief instant
  // before it does.
  return <UnsubscribeSkeleton />;
}
